import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

const trustItems = ['More leads', 'Automated follow-ups', 'Less time wasted on admin work'];

export default function Hero() {
  const heroRef = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = heroRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => setVisible(entry.isIntersecting),
      { threshold: 0.1 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={heroRef}
      className="relative min-h-screen flex flex-col justify-center items-center overflow-hidden pt-28 pb-24 px-6"
      style={{ background: 'linear-gradient(160deg, #0c2e1e 0%, #082318 50%, #061a12 100%)' }}
    >
      {/* Radial glow */}
      <div
        className="absolute top-0 left-1/2 -translate-x-1/2 w-[900px] h-[600px] pointer-events-none"
        style={{ background: 'radial-gradient(ellipse at 50% 0%, rgba(212,245,66,0.07) 0%, transparent 65%)' }}
      />

      <div className={`relative z-10 max-w-4xl w-full flex flex-col items-center text-center transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
        {/* Badge */}
        <div
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-10 text-xs font-mono uppercase tracking-widest font-semibold"
          style={{ border: '1px solid rgba(212,245,66,0.25)', background: 'rgba(212,245,66,0.07)', color: '#d4f542' }}
        >
          <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: '#d4f542' }} />
          US-Based Home Service &amp; Med Spa Specialists
        </div>

        {/* Headline */}
        <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight text-white leading-[1.05] mb-6">
          We Build Websites That Get You Clients —{' '}
          <span style={{ color: '#d4f542' }}>And AI Systems That Run Your Business</span>
        </h1>

        {/* Subheadline */}
        <p
          className="text-lg md:text-xl max-w-2xl leading-relaxed mb-10"
          style={{ color: 'rgba(255,255,255,0.6)' }}
        >
          For US-based home service, med spa &amp; dental businesses that want more leads, faster response times, and less manual work.
        </p>

        {/* CTAs */}
        <div className="flex flex-col sm:flex-row items-center gap-4 mb-14">
          <Link
            to="/contact"
            className="inline-flex items-center gap-2.5 px-8 py-4 rounded-xl text-sm font-bold transition duration-200"
            style={{ background: '#d4f542', color: '#082318' }}
            onMouseEnter={e => e.currentTarget.style.background = '#c8e830'}
            onMouseLeave={e => e.currentTarget.style.background = '#d4f542'}
          >
            Get Free Website + AI Audit
            <svg width="13" height="13" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M2 12L12 2M12 2H5M12 2v7" />
            </svg>
          </Link>
          <a
            href="#how-it-works"
            className="inline-flex items-center gap-2 px-8 py-4 rounded-xl text-sm font-semibold text-white transition duration-200 hover:bg-white/10"
            style={{ border: '1.5px solid rgba(255,255,255,0.2)' }}
          >
            See What You're Missing
          </a>
        </div>

        {/* Trust bullets */}
        <div className="flex flex-wrap justify-center gap-6">
          {trustItems.map((item, i) => (
            <div key={i} className="flex items-center gap-2 text-sm font-medium" style={{ color: 'rgba(255,255,255,0.7)' }}>
              <span
                className="flex items-center justify-center w-5 h-5 rounded-full text-xs font-bold flex-shrink-0"
                style={{ background: 'rgba(212,245,66,0.15)', color: '#d4f542' }}
              >✓</span>
              {item}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
